import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import { categories } from '@/mocks/categories';
import { getVideosByCategory, type VideoItem } from '@/mocks/videos';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'xigua');
  const [videos, setVideos] = useState<VideoItem[]>([]);

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  useEffect(() => {
    setVideos(getVideosByCategory(activeCategory));
  }, [activeCategory]);

  const currentCategory = categories.find((c) => c.slug === activeCategory);
  const categoryName = currentCategory?.name || '分类浏览';

  const handleCategoryChange = (newSlug: string) => {
    setActiveCategory(newSlug);
    window.history.replaceState(null, '', `/category/${newSlug}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${categoryName} - 茄子视频 | 高清免费在线观看`}
        description={`茄子视频${categoryName}频道，${currentCategory?.description || '高清免费在线观看'}，每日更新精彩内容。`}
        keywords={`茄子视频,${categoryName},在线影视平台,高清免费,在线观看`}
        canonicalUrl={`https://qiezi.video/category/${activeCategory}`}
      />

      <main>
        <Navbar />

        {/* Category Header */}
        <section className="pt-20 pb-8 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-4" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-950 font-medium">{categoryName}</span>
            </nav>
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-950 mb-2">
                  {categoryName}
                </h1>
                <p className="text-sm md:text-base text-foreground-600">
                  {currentCategory?.description}
                </p>
              </div>
              <span className="text-sm text-foreground-500 whitespace-nowrap">
                共 <strong className="text-foreground-950">{videos.length}</strong> 个视频
              </span>
            </div>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="sticky top-16 z-40 bg-background-50 border-b border-background-200">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-hide flex-wrap">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer flex items-center gap-2 ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50 category-tab-active'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-xs`}></i>
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Video Grid */}
        <section className="py-8 md:py-12 bg-background-50">
          <div className="w-full px-4 md:px-6">
            {videos.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                {videos.map((video, idx) => (
                  <Link
                    key={video.id}
                    to={`/article/${video.id}`}
                    className="group bg-background-50 rounded-lg overflow-hidden transition-all duration-300 cursor-pointer video-card"
                  >
                    <div className="relative aspect-[16/9] overflow-hidden">
                      <img
                        src={video.cover}
                        alt={video.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="video-card-overlay absolute inset-0 bg-black/30 opacity-0 transition-opacity duration-300 flex items-center justify-center">
                        <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500">
                          <i className="ri-play-fill text-background-50 text-xl"></i>
                        </div>
                      </div>
                      <span className="absolute bottom-2 right-2 px-2 py-0.5 bg-black/70 text-background-50 text-xs rounded whitespace-nowrap">
                        {video.duration}
                      </span>
                    </div>
                    <div className="p-3 md:p-4">
                      <h3 className="text-sm md:text-base font-semibold text-foreground-950 mb-2 line-clamp-1 group-hover:text-primary-600 transition-colors">
                        {video.title}
                      </h3>
                      <p className="text-xs text-foreground-600 line-clamp-1 mb-2">{video.description}</p>
                      <div className="flex items-center gap-3 text-xs text-foreground-500 flex-wrap">
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line"></i> {(video.views / 10000).toFixed(1)}万
                        </span>
                        <span>{video.publishDate}</span>
                        <span className="whitespace-nowrap">{video.categoryName}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
                  <i className="ri-video-line text-foreground-400 text-2xl"></i>
                </div>
                <h3 className="text-lg font-semibold text-foreground-700 mb-2">暂无视频</h3>
                <p className="text-sm text-foreground-500">该分类下还没有视频内容，请浏览其他分类</p>
                <Link
                  to="/category/xigua"
                  className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  浏览西瓜视频
                </Link>
              </div>
            )}
          </div>
        </section>

        {/* Other Categories */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <h2 className="text-lg md:text-2xl font-bold font-heading text-foreground-950 mb-6">
              探索更多频道
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
              {categories
                .filter((c) => c.slug !== activeCategory)
                .map((cat) => (
                  <button
                    key={cat.slug}
                    onClick={() => handleCategoryChange(cat.slug)}
                    className="p-4 rounded-lg bg-background-50 hover:bg-primary-50 transition-all duration-300 cursor-pointer text-center"
                  >
                    <div className={`w-10 h-10 mx-auto mb-2 flex items-center justify-center rounded-full bg-gradient-to-br ${cat.color}`}>
                      <i className={`${cat.icon} text-background-50 text-base`}></i>
                    </div>
                    <span className="text-sm font-medium text-foreground-950 font-heading">{cat.name}</span>
                  </button>
                ))}
            </div>
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
}